import { keyBy, maxBy } from 'es-toolkit';
import type { IconName } from '@insio/ui-toolkit';
import { navigation } from '@/navigation';

/** Groups of the Nastavení kanceláře menu, in the order they are shown. */
export const MASTER_ACCOUNT_SETTINGS_GROUP_IDS = [
  'office',
  'clientIntegrations',
  'subscription'
] as const;

export type MasterAccountSettingsGroupId = (typeof MASTER_ACCOUNT_SETTINGS_GROUP_IDS)[number];

/** Marks a section shown below the groups, set apart and without a group. */
export const MASTER_ACCOUNT_SETTINGS_FOOTER = 'footer';

interface MasterAccountSettingsSectionDefinition {
  readonly id: string;
  readonly group: MasterAccountSettingsGroupId | typeof MASTER_ACCOUNT_SETTINGS_FOOTER;
  readonly icon: IconName;
  /** The path the section opens at; its sub-pages live below it. */
  readonly path: () => string;
}

/** Sections of Nastavení kanceláře in menu order; the index opens the first one allowed. */
export const MASTER_ACCOUNT_SETTINGS_SECTIONS = [
  { id: 'office', group: 'office', icon: 'building', path: navigation.masterAccountOfficeSettings },
  { id: 'people', group: 'office', icon: 'user', path: navigation.masterAccountPeople },
  {
    id: 'integrationTemplates',
    group: 'clientIntegrations',
    icon: 'copy',
    path: navigation.masterAccountIntegrationTemplates
  },
  {
    id: 'bridges',
    group: 'clientIntegrations',
    icon: 'server',
    path: navigation.masterAccountBridges
  },
  {
    id: 'billing',
    group: 'subscription',
    icon: 'credit-card',
    path: navigation.masterAccountBilling
  },
  {
    id: 'credits',
    group: 'subscription',
    icon: 'list-ul',
    path: navigation.masterAccountCreditTransactions
  },
  {
    id: 'manage',
    group: MASTER_ACCOUNT_SETTINGS_FOOTER,
    icon: 'gear',
    path: navigation.masterAccountManage
  }
] as const satisfies readonly MasterAccountSettingsSectionDefinition[];

export type MasterAccountSettingsSection = (typeof MASTER_ACCOUNT_SETTINGS_SECTIONS)[number];
export type MasterAccountSettingsSectionId = MasterAccountSettingsSection['id'];

const SECTION_BY_ID = keyBy(MASTER_ACCOUNT_SETTINGS_SECTIONS, section => section.id);

/** The section of an id. */
export function masterAccountSettingsSection(
  id: MasterAccountSettingsSectionId
): MasterAccountSettingsSection {
  return SECTION_BY_ID[id];
}

/** The path a section opens at. */
export function masterAccountSettingsSectionPath(id: MasterAccountSettingsSectionId): string {
  return SECTION_BY_ID[id].path();
}

/** The section a pathname belongs to — its own path or one below it, longest wins — or null. */
export function masterAccountSettingsSectionOf(
  pathname: string
): MasterAccountSettingsSectionId | null {
  const containing = MASTER_ACCOUNT_SETTINGS_SECTIONS.filter(section => {
    const path = section.path();
    return pathname === path || pathname.startsWith(`${path}/`);
  });
  return maxBy(containing, section => section.path().length)?.id ?? null;
}
